import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../context/AuthContext";
import { AuthShell, FieldLabel, FormError, PrivateNotice } from "../components/auth/AuthShell";
import { BUSINESS } from "../config/business";

const TOKEN_KEY = "bj_admin_token";

const LoginPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState("credentials"); // credentials | enroll | verify
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [code, setCode] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [manualSecret, setManualSecret] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) {
      const to = location.state?.from?.pathname || "/";
      navigate(to, { replace: true });
    }
  }, [user, navigate, location.state]);

  const onCredentials = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email: email.trim(), password });
      setChallengeToken(data.challenge_token);
      if (data.step === "enroll") {
        setQrDataUrl(data.qr_data_url);
        setManualSecret(data.manual_secret);
        setStep("enroll");
      } else {
        setStep("verify");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Verify credentials.");
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/verify`, {
        challenge_token: challengeToken,
        code: code.trim(),
      });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      // hard reload triggers AuthContext to re-verify with the new token, then redirects
      window.location.replace(location.state?.from?.pathname || "/");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep("credentials");
    setCode("");
    setChallengeToken("");
    setQrDataUrl("");
    setManualSecret("");
    setError("");
  };

  const onSecondStep = step !== "credentials";
  const tabs = [
    { id: "credentials", label: "1 · Credentials", active: !onSecondStep },
    { id: "second", label: step === "enroll" ? "2 · 2FA setup" : "2 · 2FA code", active: onSecondStep },
  ];

  return (
    <AuthShell testid="login-page">
      <div className="grid grid-cols-2 border-b border-[rgba(42,28,21,0.18)] mb-7" role="tablist">
        {tabs.map((t) => (
          <div
            key={t.id}
            role="tab"
            aria-selected={t.active}
            data-testid={`login-tab-${t.id}`}
            className={`pb-3 -mb-px text-sm border-b ${t.active ? "border-[#6E5022] text-[#2A1C15] font-medium" : "border-transparent text-[#5E4D40]"}`}
          >
            {t.label}
          </div>
        ))}
      </div>

      {step === "credentials" && (
        <>
          <h2 className="text-lg font-medium mb-6">Administrator login</h2>
          <form onSubmit={onCredentials} className="flex flex-col gap-5">
            <div>
              <FieldLabel htmlFor="login-email">Email</FieldLabel>
              <input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pr-input"
                placeholder={BUSINESS.email}
                autoComplete="email"
              />
            </div>
            <div>
              <FieldLabel htmlFor="login-password">Password</FieldLabel>
              <div className="relative">
                <input
                  id="login-password"
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-input pr-16"
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                />
                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw((v) => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-xs pr-link" aria-label="toggle password visibility">
                  {showPw ? "Hide" : "Show"}
                </button>
              </div>
              <div className="mt-3 text-right">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="pr-link text-sm">
                  Forgot password?
                </Link>
              </div>
            </div>
            {error && <FormError testid="login-error">{error}</FormError>}
            <button type="submit" disabled={loading} data-testid="login-submit-button" className="pr-btn w-full">
              {loading ? "Authenticating…" : "Continue"}
            </button>
          </form>
        </>
      )}

      {step === "enroll" && (
        <>
          <h2 className="text-lg font-medium mb-3">Set up two-step verification</h2>
          <p className="text-[#5E4D40] text-sm mb-4 leading-relaxed">
            Scan the QR code with Google Authenticator, 1Password, Authy, or any TOTP app. Then enter the 6-digit code to finish enrollment.
          </p>
          <div className="flex flex-col items-center my-5">
            {qrDataUrl && (
              <img data-testid="enroll-qr" src={qrDataUrl} alt="TOTP QR" className="w-44 h-44 border border-[rgba(42,28,21,0.18)] bg-[#F2EBDD] p-1" />
            )}
            <div className="mt-4 text-center">
              <div className="text-xs text-[#5E4D40] mb-1">Or enter manually</div>
              <code data-testid="enroll-manual-secret" className="text-[#2A1C15] text-xs tracking-widest break-all bg-[#E7DDCB] px-2 py-1">
                {manualSecret}
              </code>
            </div>
          </div>
          <form onSubmit={onVerify} className="flex flex-col gap-5">
            <div>
              <FieldLabel htmlFor="enroll-code">6-digit code from app</FieldLabel>
              <input
                id="enroll-code"
                data-testid="enroll-code-input"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="pr-input text-center text-2xl tracking-[0.4em] bj-tnum"
                placeholder="••••••"
              />
            </div>
            {error && <FormError testid="login-error">{error}</FormError>}
            <button type="submit" disabled={loading || code.length !== 6} data-testid="enroll-verify-button" className="pr-btn w-full">
              {loading ? "Verifying…" : "Finish enrollment"}
            </button>
            <button type="button" onClick={reset} data-testid="enroll-back-button" className="pr-link text-sm self-center">Back</button>
          </form>
        </>
      )}

      {step === "verify" && (
        <>
          <h2 className="text-lg font-medium mb-3">Two-step verification</h2>
          <p className="text-[#5E4D40] text-sm mb-5">Open your authenticator app and enter the 6-digit code.</p>
          <form onSubmit={onVerify} className="flex flex-col gap-5">
            <input
              data-testid="verify-code-input"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoComplete="one-time-code"
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="pr-input text-center text-2xl tracking-[0.4em] bj-tnum"
              placeholder="••••••"
            />
            {error && <FormError testid="login-error">{error}</FormError>}
            <button type="submit" disabled={loading || code.length !== 6} data-testid="verify-submit-button" className="pr-btn w-full">
              {loading ? "Verifying…" : "Access dashboard"}
            </button>
            <button type="button" onClick={reset} data-testid="verify-back-button" className="pr-link text-sm self-center">Back to login</button>
          </form>
        </>
      )}

      <PrivateNotice />
    </AuthShell>
  );
};

export default LoginPage;
